import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarPlus, CalendarCheck, Clock, Prohibit } from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function Leave() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ leave_type: "Vacation", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leaves/mine");
      setData(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setForm({ leave_type: "Vacation", start_date: "", end_date: "", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const bal = data?.balance;
  const credits = data?.credits;

  return (
    <div className="space-y-6" data-testid="leave-page">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Time Off</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">My Leave</h1>
        <p className="text-muted-foreground mt-2">Request time off and track your balance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-border shadow-none" data-testid="credit-vl-card">
          <CalendarCheck weight="duotone" className="h-6 w-6 text-primary" />
          <p className={`mt-3 text-2xl font-heading font-extrabold ${(credits?.vl ?? 0) < 0 ? "text-destructive" : ""}`} data-testid="credit-vl-value">{credits?.vl ?? "—"}</p>
          <p className="text-sm text-muted-foreground">Vacation (VL)</p>
        </Card>
        <Card className="p-5 border-border shadow-none" data-testid="credit-sl-card">
          <CalendarCheck weight="duotone" className="h-6 w-6 text-warning" />
          <p className={`mt-3 text-2xl font-heading font-extrabold ${(credits?.sl ?? 0) < 0 ? "text-destructive" : ""}`} data-testid="credit-sl-value">{credits?.sl ?? "—"}</p>
          <p className="text-sm text-muted-foreground">Sick (SL)</p>
        </Card>
        <Card className="p-5 border-border shadow-none" data-testid="credit-others-card">
          <CalendarCheck weight="duotone" className="h-6 w-6 text-success" />
          <p className={`mt-3 text-2xl font-heading font-extrabold ${(credits?.others ?? 0) < 0 ? "text-destructive" : ""}`} data-testid="credit-others-value">{credits?.others ?? "—"}</p>
          <p className="text-sm text-muted-foreground">Others</p>
        </Card>
        <Card className="p-5 border-border shadow-none" data-testid="credit-pending-card">
          <Clock weight="duotone" className="h-6 w-6 text-muted-foreground" />
          <p className="mt-3 text-2xl font-heading font-extrabold">{bal?.pending ?? "—"}</p>
          <p className="text-sm text-muted-foreground">Pending days</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border-border shadow-none lg:col-span-1 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <CalendarPlus weight="duotone" className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-bold">Request leave</h2>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
            <div className="space-y-1.5">
              <Label>Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vacation">Vacation Leave (VL)</SelectItem>
                  <SelectItem value="Sick">Sick Leave (SL)</SelectItem>
                  <SelectItem value="Others">Others</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input data-testid="leave-start" type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>End date</Label>
              <Input data-testid="leave-end" type="date" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea data-testid="leave-reason" placeholder="Brief reason..." value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })} required />
            </div>
            <Button type="submit" data-testid="submit-leave-button" disabled={busy} className="w-full font-semibold">
              {busy ? "Submitting..." : "Submit request"}
            </Button>
          </form>
        </Card>

        <Card className="border-border shadow-none lg:col-span-2 overflow-hidden">
          <div className="p-4 border-b border-border"><h2 className="font-heading font-bold">Request history</h2></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data?.requests?.length ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                    <Prohibit className="h-6 w-6 mx-auto mb-2 opacity-40" />No requests yet.
                  </TableCell></TableRow>
                ) : data.requests.map((r) => (
                  <TableRow key={r.id} data-testid={`leave-request-${r.id}`} className="animate-fade-up">
                    <TableCell className="font-medium">{r.leave_type}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{r.start_date} → {r.end_date}</TableCell>
                    <TableCell>{r.days}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`capitalize ${STATUS_BADGE[r.status]}`}>{r.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
